import { useEffect, useRef, useState } from "react";
import { formatBpm } from "@web/lib/format.ts";
import { cn } from "@web/lib/utils.ts";

function parseBpmDraft(raw: string): number | null | "invalid" {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed.replace(",", "."));
  if (!Number.isFinite(n) || n < 1 || n > 400) return "invalid";
  return Math.round(n * 10) / 10;
}

/**
 * Overview BPM chip. Double-click to type a per-user override.
 * Empty commit clears the override so enrichment can show through again.
 */
export function BpmBadge({
  bpm,
  manual,
  disabled,
  onCommit,
}: {
  bpm: number | null;
  manual: boolean;
  disabled: boolean;
  onCommit: (next: number | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  function startEdit() {
    if (disabled) return;
    setDraft(bpm === null ? "" : formatBpm(bpm));
    setEditing(true);
  }

  function commit() {
    const parsed = parseBpmDraft(draft);
    setEditing(false);
    if (parsed === "invalid") return;
    const current = bpm === null ? null : Math.round(bpm * 10) / 10;
    if (parsed === current) return;
    onCommit(parsed);
  }

  if (editing) {
    return (
      <label className="inline-flex items-center gap-1 rounded-full border border-accent bg-panel px-2 py-0.5">
        <input
          ref={inputRef}
          type="text"
          inputMode="decimal"
          aria-label="BPM"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            }
            if (e.key === "Escape") {
              e.preventDefault();
              setEditing(false);
            }
          }}
          className="h-4 w-10 bg-transparent text-center text-xs font-medium text-text tabular-nums outline-none"
        />
        <span className="text-xs font-medium text-faint">BPM</span>
      </label>
    );
  }

  return (
    <button
      type="button"
      title={manual ? "Manual BPM — double-click to edit" : "Double-click to set BPM"}
      disabled={disabled}
      onDoubleClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        startEdit();
      }}
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium tabular-nums",
        manual
          ? "border-status-working/40 bg-panel text-status-working"
          : "border-line bg-panel text-faint",
      )}
    >
      {formatBpm(bpm)} BPM
    </button>
  );
}
